import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProcedureDocument, DocumentNode, Comment as ReviewComment, User } from '@completion/models';
import { getCurrentDocument, selectReviewerMode } from '@completion/selectors';

interface PanelComment {
  id: number;
  nodeId: number;
  nodeName: string;
  nodeType: string;
  content: string;
  authorName: string;
  createdAt: string;
  selectionText?: string;
}

@Component({
  selector: 'app-comments-panel',
  templateUrl: './comments-panel.component.html',
  styleUrls: ['./comments-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommentsPanelComponent {
  reviewerMode$: Observable<boolean> = this.store.select(selectReviewerMode);
  comments$: Observable<PanelComment[]> = this.store.select(getCurrentDocument).pipe(map((doc) => this.flattenComments(doc)));

  constructor(private store: Store) {}

  trackById(_index: number, item: PanelComment): number {
    return item?.id ?? _index;
  }

  private flattenComments(doc: ProcedureDocument | null | undefined): PanelComment[] {
    if (!doc || !Array.isArray(doc.nodes)) return [];
    const out: PanelComment[] = [];

    const walk = (nodes: DocumentNode[]) => {
      for (const n of nodes) {
        if (!n) continue;
        const nodeName = (n.name || '').toString();
        const nodeType = n.type != null ? String(n.type) : '';
        const comments: ReviewComment[] = Array.isArray(n.comments) ? (n.comments as any) : [];
        for (const c of comments) {
          const authorName =
            (c.author &&
              ((c.author as User).name || (c.author as any).displayName || (c.author as any).userName || (c.author as any).email)) ||
            'Unknown';
          const flat: PanelComment = {
            id: c.id,
            nodeId: n.id,
            nodeName,
            nodeType,
            content: c.content || '',
            authorName,
            createdAt: c.createdAt || '',
            selectionText: (c as any).selectionText,
          };
          out.push(flat);
        }
        if (Array.isArray(n.children) && n.children.length) {
          walk(n.children);
        }
      }
    };

    walk(doc.nodes);
    // Sort newest first
    out.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    return out;
  }
}
